// pages/customers/CustomersPage.tsx
import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { showError } from '@/lib/error'
import { useCustomers, useDeleteCustomer, usePrepareManualOrder } from '@/hooks/useCustomers'
import { useOrderCreateStore } from '@/stores/order-create'
import { usePermissionStore } from '@/stores/permission'
import { CustomerSearchBar } from './CustomerSearchBar'
import { CustomersTable } from './CustomersTable'
import {
  CreateCustomerDialog,
  CustomerDetailDrawer,
  EditCustomerDialog,
  TransferDialog,
} from '@/components/customers'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import type { Customer, CustomerFilter } from '@/types/customer'
import { customerCategoriesApi } from '@/api/customer-categories'
import { ServiceRecordDialog } from '@/components/service-records/ServiceRecordDialog'

export function CustomersPage() {
  const navigate = useNavigate()
  const isSuperAdmin = usePermissionStore((state) => state.isSuperAdmin)
  const [searchParams, setSearchParams] = useSearchParams()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [recordDialogOpen, setRecordDialogOpen] = useState(false)
  const [recordCustomer, setRecordCustomer] = useState<Customer | null>(null)
  const queryClient = useQueryClient()
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [preparingCustomerId, setPreparingCustomerId] = useState<number | null>(null)
  const isPreparingManualOrderRef = useRef(false)
  const { data: categoryOptions = [] } = useQuery({
    queryKey: ['customer-categories', 'options'],
    queryFn: customerCategoriesApi.options,
  })
  const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get('page_size') || String(DEFAULT_PAGE_SIZE), 10))

  // 搜索参数
  const [searchValues, setSearchValues] = useState<CustomerFilter & { date_range?: [string, string] }>(() => {
    const params: CustomerFilter & { date_range?: [string, string] } = { scope: 'my' }
    const name = searchParams.get('name')
    const phone = searchParams.get('phone')
    const wechat = searchParams.get('wechat')
    const scope = searchParams.get('scope')
    const department_id = searchParams.get('department_id')
    const start_date = searchParams.get('start_date')
    const end_date = searchParams.get('end_date')
    const category_id = searchParams.get('category_id')
    const vip_level = searchParams.get('vip_level')
    const user_ids = searchParams.get('user_ids')
    const tracker_ids = searchParams.get('tracker_ids')
    const blacklist = searchParams.get('blacklist')

    if (name) params.name = name
    if (phone) params.phone = phone
    if (wechat) params.wechat = wechat
    if (scope === 'department') {
      params.scope = 'department'
      const departmentID = Number(department_id)
      if (Number.isInteger(departmentID) && departmentID > 0) {
        params.department_id = departmentID
      }
    } else if (scope === 'unassigned') {
      params.scope = 'unassigned'
    }
    if (start_date && end_date) {
      params.start_date = start_date
      params.end_date = end_date
      params.date_range = [start_date, end_date]
    }
    const categoryID = Number(category_id)
    if (Number.isInteger(categoryID) && categoryID > 0) {
      params.category_id = categoryID
    }
    const vipLevel = Number(vip_level)
    if (vip_level !== null && Number.isInteger(vipLevel)) {
      params.vip_level = vipLevel
    }
    if (user_ids) {
      params.user_ids = user_ids.split(',').map(Number).filter((id) => Number.isInteger(id) && id > 0)
    }
    if (tracker_ids) {
      params.tracker_ids = tracker_ids.split(',').map(Number).filter((id) => Number.isInteger(id) && id > 0)
    }
    if (blacklist === 'true' || blacklist === 'false') {
      params.blacklist = blacklist === 'true'
    }

    return params
  })

  const page = parseInt(searchParams.get('page') || '1', 10)

  // 查询
  const { data, isLoading, isError, error, refetch, isFetching } = useCustomers({
    page,
    page_size: pageSize,
    ...searchValues,
  })

  const deleteMutation = useDeleteCustomer()
  const prepareManualOrderMutation = usePrepareManualOrder()

  // 同步到 URL
  const syncToUrl = (params: typeof searchValues, newPage = 1, newPageSize = pageSize) => {
    const urlParams = new URLSearchParams()
    if (params.name) urlParams.set('name', params.name)
    if (params.phone) urlParams.set('phone', params.phone)
    if (params.wechat) urlParams.set('wechat', params.wechat)
    if (params.scope === 'department') {
      urlParams.set('scope', params.scope)
      if (
        typeof params.department_id === 'number' &&
        Number.isInteger(params.department_id) &&
        params.department_id > 0
      ) {
        urlParams.set('department_id', String(params.department_id))
      }
    } else if (params.scope === 'unassigned') {
      urlParams.set('scope', 'unassigned')
    }
    if (params.start_date) urlParams.set('start_date', params.start_date)
    if (params.end_date) urlParams.set('end_date', params.end_date)
    if (typeof params.category_id === 'number' && params.category_id > 0) {
      urlParams.set('category_id', String(params.category_id))
    }
    if (typeof params.vip_level === 'number') {
      urlParams.set('vip_level', String(params.vip_level))
    }
    if (params.user_ids && params.user_ids.length > 0) {
      urlParams.set('user_ids', params.user_ids.join(','))
    }
    if (params.tracker_ids && params.tracker_ids.length > 0) {
      urlParams.set('tracker_ids', params.tracker_ids.join(','))
    }
    if (typeof params.blacklist === 'boolean') {
      urlParams.set('blacklist', String(params.blacklist))
    }
    if (newPage > 1) urlParams.set('page', String(newPage))
    if (newPageSize !== DEFAULT_PAGE_SIZE) urlParams.set('page_size', String(newPageSize))
    setSearchParams(urlParams, { replace: true })
  }

  const handleSearch = () => {
    syncToUrl(searchValues, 1)
  }

  const handleReset = () => {
    setSearchValues({ scope: 'my' })
    syncToUrl({}, 1)
  }

  const handlePageChange = (newPage: number) => {
    syncToUrl(searchValues, newPage)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    syncToUrl(searchValues, 1, newPageSize)
  }

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer)
    setEditDialogOpen(true)
  }

  const handleViewDetail = (customer: Customer) => {
    setSelectedCustomer(customer)
    setDetailDrawerOpen(true)
  }

  const handleDelete = (customer: Customer) => {
    setSelectedCustomer(customer)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!selectedCustomer) return
    try {
      await deleteMutation.mutateAsync(selectedCustomer.id)
      setDeleteDialogOpen(false)
      setSelectedCustomer(null)
      refetch()
    } catch (error) {
      showError(error, '删除客户失败')
    }
  }

  const handleTransfer = (customer: Customer) => {
    setSelectedCustomer(customer)
    setTransferDialogOpen(true)
  }

  const handleRecordService = (customer: Customer) => {
    setRecordCustomer(customer)
    setRecordDialogOpen(true)
  }

  const handleManualOrder = async (customer: Customer) => {
    if (isPreparingManualOrderRef.current) return

    isPreparingManualOrderRef.current = true
    setPreparingCustomerId(customer.id)

    try {
      const crmebUser = await prepareManualOrderMutation.mutateAsync(customer.id)
      const orderStore = useOrderCreateStore.getState()
      orderStore.reset()
      orderStore.setSelectedUser(crmebUser)
      navigate(`/admin/orders/create?from_customer_id=${customer.id}&uid=${crmebUser.uid}`)
    } catch (error) {
      showError(error, '准备手动录单失败')
    } finally {
      isPreparingManualOrderRef.current = false
      setPreparingCustomerId(null)
    }
  }

  const isPreparingManualOrder = prepareManualOrderMutation.isPending || preparingCustomerId !== null

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">客户管理</h1>
          <p className="text-muted-foreground">管理所有客户信息</p>
        </div>
        <PermissionGuard permission="customer:create">
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            录入客户
          </Button>
        </PermissionGuard>
      </div>

      {/* 搜索筛选 */}
      <Card>
        <CardContent className="pt-6">
          <CustomerSearchBar
            value={searchValues}
            onChange={setSearchValues}
            onSearch={handleSearch}
            onReset={handleReset}
            categoryOptions={categoryOptions}
            enableUnassigned={isSuperAdmin}
          />
        </CardContent>
      </Card>

      {/* 客户表格 */}
      <Card>
        <CardContent className="p-0">
          {isError ? (
            <div className="p-8 text-center">
              <p className="text-destructive">加载失败: {(error as Error).message}</p>
              <Button variant="outline" className="mt-4" onClick={() => refetch()}>
                重试
              </Button>
            </div>
          ) : (
            <CustomersTable
              data={data?.list}
              isLoading={isLoading}
              onEdit={handleEdit}
              onViewDetail={handleViewDetail}
              onDelete={handleDelete}
              onTransfer={handleTransfer}
              onManualOrder={handleManualOrder}
              onRecordService={handleRecordService}
              preparingCustomerId={preparingCustomerId}
              isPreparingManualOrder={isPreparingManualOrder}
            />
          )}
        </CardContent>
      </Card>

      {/* 分页 */}
      <ListPagination
        page={page}
        pageSize={pageSize}
        total={data?.total || 0}
        loading={isFetching}
        showPageNumbers={false}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* 创建客户对话框 */}
      <CreateCustomerDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => refetch()}
      />

      {/* 编辑客户对话框 */}
      <EditCustomerDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        customer={selectedCustomer}
        onSuccess={() => {
          setSelectedCustomer(null)
          refetch()
        }}
      />

      {/* 客户详情抽屉 */}
      <CustomerDetailDrawer
        open={detailDrawerOpen}
        onOpenChange={setDetailDrawerOpen}
        customerId={selectedCustomer?.id}
      />

      {/* 录入服务对话框 */}
      <ServiceRecordDialog
        open={recordDialogOpen}
        onOpenChange={setRecordDialogOpen}
        customer={recordCustomer ?? undefined}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['customers'] })
        }}
      />

      {/* 转移客户对话框 */}
      <TransferDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        customer={selectedCustomer}
        onSuccess={() => {
          setSelectedCustomer(null)
          refetch()
        }}
      />

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除</DialogTitle>
            <DialogDescription>
              确定要删除客户"{selectedCustomer?.name}"吗？此操作不可撤销。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSelectedCustomer(null)
              setDeleteDialogOpen(false)
            }}>
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '删除中...' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
